import { useCallback, useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ChevronLeft, Inbox, Loader2, Plus } from "lucide-react";

const STATUS = {
  open: ["Bekliyor", "bg-amber-100 text-amber-800"],
  answered: ["Cevaplandı", "bg-emerald-100 text-emerald-800"],
  closed: ["Kapandı", "bg-zinc-100 text-zinc-600"],
};
export const StatusPill = ({ s }) => <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS[s]?.[1]}`}>{STATUS[s]?.[0]}</span>;

const Thread = ({ t, onBack, onChange }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { const r = await api.post(`/support/tickets/${t.id}/reply`, { text: text.trim() }); setText(""); onChange(r.data); }
    catch { toast.error("Gönderilemedi"); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col h-full min-h-0">
      <button onClick={onBack} data-testid="ticket-back" className="flex items-center gap-1 px-4 py-2 text-xs font-medium text-emerald-700 border-b border-zinc-100"><ChevronLeft className="h-3.5 w-3.5" />Taleplerim</button>
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3 bg-zinc-50/70" data-testid="ticket-thread">
        <div className="rounded-2xl rounded-br-md bg-[#062a20] px-3.5 py-2 text-sm text-white whitespace-pre-wrap ml-auto max-w-[85%]">{t.message}</div>
        {t.replies.map((r, i) => (
          <div key={i} className={r.from === "admin" ? "max-w-[90%] rounded-2xl rounded-bl-md bg-white px-3.5 py-2 ring-1 ring-emerald-200" : "ml-auto max-w-[85%] rounded-2xl rounded-br-md bg-[#062a20] px-3.5 py-2 text-white"}>
            {r.from === "admin" && <p className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 mb-0.5">Destek ekibi</p>}
            <p className="text-sm whitespace-pre-wrap">{r.text}</p>
            <p className={`text-[10px] mt-1 ${r.from === "admin" ? "text-zinc-400" : "text-emerald-100/60"}`}>{formatDate(r.at)}</p>
          </div>
        ))}
        {t.status === "open" && t.replies.every((r) => r.from !== "admin") && <p className="text-xs text-zinc-400 text-center">Talebiniz alındı, en kısa sürede cevaplanacak.</p>}
      </div>
      <div className="border-t border-zinc-200 bg-white p-3 flex items-end gap-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={1} placeholder="Ek bilgi yazın…" data-testid="ticket-reply-input" className="min-h-[40px] max-h-24 resize-none text-sm" />
        <Button onClick={send} disabled={busy || !text.trim()} data-testid="ticket-reply-send" className="h-10 bg-emerald-700 hover:bg-emerald-800 text-white text-xs">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gönder"}</Button>
      </div>
    </div>
  );
};

const NewTicket = ({ onDone }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (text.trim().length < 3) { toast.error("Lütfen sorunuzu yazın"); return; }
    setBusy(true);
    try { await api.post("/support/tickets", { message: text.trim(), page: window.location.pathname }); toast.success("Destek talebiniz iletildi"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gönderilemedi"); } finally { setBusy(false); }
  };
  return (
    <div className="p-4 space-y-2">
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} maxLength={3000} autoFocus placeholder="Sorununuzu veya talebinizi yazın…" data-testid="new-ticket-text" className="text-sm" />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onDone}>Vazgeç</Button>
        <Button size="sm" onClick={send} disabled={busy} data-testid="new-ticket-send" className="bg-emerald-700 hover:bg-emerald-800 text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Gönder"}</Button>
      </div>
    </div>
  );
};

export const TicketsTab = ({ refreshKey, onSeen }) => {
  const [list, setList] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => api.get("/support/tickets").then((r) => setList(r.data)).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const open = (t) => {
    setOpenId(t.id);
    if (t.unread) api.post(`/support/tickets/${t.id}/seen`).then(() => { setList((l) => l.map((x) => (x.id === t.id ? { ...x, unread: false } : x))); onSeen?.(); }).catch(() => {});
  };
  const current = list?.find((t) => t.id === openId);
  if (current) return <Thread t={current} onBack={() => setOpenId(null)} onChange={(nt) => setList((l) => l.map((x) => (x.id === nt.id ? nt : x)))} />;
  if (creating) return <NewTicket onDone={() => { setCreating(false); load(); }} />;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-100">
        <p className="text-xs text-zinc-500">Destek ekibine ilettiğiniz sorular</p>
        <Button size="sm" variant="outline" onClick={() => setCreating(true)} data-testid="new-ticket-btn" className="h-7 gap-1 text-xs"><Plus className="h-3.5 w-3.5" />Yeni talep</Button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-zinc-100" data-testid="tickets-list">
        {list === null && <p className="p-6 text-center text-xs text-zinc-400">Yükleniyor…</p>}
        {list?.length === 0 && <div className="p-8 text-center"><Inbox className="h-8 w-8 mx-auto text-zinc-300" strokeWidth={1.25} /><p className="mt-2 text-xs text-zinc-500">Henüz destek talebiniz yok.</p></div>}
        {list?.map((t) => (
          <button key={t.id} onClick={() => open(t)} data-testid={`ticket-${t.id}`} className="w-full text-left px-4 py-3 hover:bg-zinc-50 transition-colors">
            <div className="flex items-center gap-2">
              {t.unread && <span className="h-2 w-2 rounded-full bg-rose-500" data-testid={`ticket-unread-${t.id}`} />}
              <StatusPill s={t.status} />
              <span className="ml-auto text-[10px] text-zinc-400">{formatDate(t.updated_at)}</span>
            </div>
            <p className="mt-1 text-sm text-zinc-800 line-clamp-2">{t.message}</p>
          </button>
        ))}
      </div>
    </div>
  );
};
